import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Menu, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { IconChevronDown, IconShieldLock } from "@tabler/icons";

// project imports
import ScrollRail, { PageHeader, pillSx } from "ui-component/extended/ScrollRail";
import { getAdmin } from "menu-items/Admin";

/* ---------- Flatten Helper ---------- */
export function flattenMenuItems(items, out = []) {
  if (!Array.isArray(items)) return out;

  items.forEach((item) => {
    if (item.type === "item" && item.url) {
      out.push({ id: item.id, title: item.title, url: item.url });
    }
    if (item.children?.length) {
      flattenMenuItems(item.children, out);
    }
  });

  return out;
}

export function isAdminPath(pathname) {
  // From getAdmin() rather than a module constant, so this reads the same tree
  // the rail below renders. If the two could disagree, the strip could appear on
  // a path its own rail does not recognise. flattenMenuItems guards the
  // non-array case itself.
  const items = flattenMenuItems(getAdmin().children);
  const path = (pathname || "").split("?")[0];

  return items.some(
    (item) => path === item.url || (item.url && path.startsWith(item.url + "/"))
  );
}

/*
 * One row in the dropdown — a page, with the marker and icon that say whether it
 * is the page you are on.
 *
 * `selectedUrl` is passed in rather than each row testing the path itself. A row
 * cannot answer the question alone: under qic → QicEvent, /qic_event sits beside
 * /qic_event/stages, so a prefix test lights up both on the deeper page. One
 * winner is computed in AdminMenu where the whole tree is visible.
 */
function ItemRow({ item, onNavigate, selectedUrl }) {
  const theme = useTheme();
  const blue = theme.palette.secondary.main;
  const isSelected = Boolean(item.url) && item.url === selectedUrl;
  const ItemIcon = item.icon;

  return (
    <Box
      onClick={() => onNavigate(item.url)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.2,
        borderRadius: 2,
        cursor: "pointer",
        position: "relative",
        transition: "all 0.2s ease",
        // The glyphs below inherit this through `currentColor`, so the row states
        // its colour once. The label overrides it — it sits darker than its icon,
        // as it did before.
        color: isSelected ? 'secondary.main' : 'text.secondary',
        // The same 0.08 the rail's own capsule is drawn in, so this row's tint
        // and the rail's tint are literally the same colour.
        backgroundColor: isSelected ? alpha(blue, 0.08) : "transparent",
        "&:hover": {
          backgroundColor: alpha(blue, 0.05),
          transform: "translateX(4px)",
        },
      }}
    >
      {isSelected && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 6,
            bottom: 6,
            width: 4,
            borderRadius: 2,
            bgcolor: 'secondary.main',
          }}
        />
      )}
      {ItemIcon ? (
        <ItemIcon size={18} stroke={1.5} />
      ) : (
        <FiberManualRecordIcon
          sx={{
            fontSize: 8,
            // Damped rather than `inherit`: this bullet stands in for a missing
            // icon and was drawn fainter than the icons beside it.
            color: isSelected ? 'secondary.main' : alpha(theme.palette.text.secondary, 0.6),
          }}
        />
      )}
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: isSelected ? 600 : 500,
          color: isSelected ? "secondary.main" : "text.primary",
        }}
      >
        {item.title}
      </Typography>
    </Box>
  );
}

/* ---------- Component ---------- */
function AdminMenu() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname || "";
  const path = pathname.split("?")[0];

  // Read per render rather than at import: the tree is a function of the stored
  // role, and getAdmin() memoises it, so this is the same array every time until
  // the role itself changes — which is what lets the effects below depend on it.
  //
  // No `|| []` fallback: buildAdminTree always returns an array, and a fallback
  // here would hand the effects a NEW array on every render whenever it fired,
  // re-running them each pass.
  const topItems = getAdmin().children;
  const displayItems = topItems;

  /*
   * The one page the current url belongs to.
   *
   * Longest match wins. A prefix test alone would light up two rows wherever one
   * sibling's url is a prefix of another's, which is the case under
   * qic → QicEvent: /qic_event sits beside /qic_event/stages.
   */
  const selectedUrl = useMemo(
    () =>
      flattenMenuItems(topItems)
        .map((item) => item.url)
        .filter((url) => path === url || path.startsWith(url + "/"))
        .sort((a, b) => b.length - a.length)[0],
    [path, topItems]
  );

  const computedIndex = useMemo(() => {
    const idx = topItems.findIndex((item) => {
      if (item.type === "item" && item.url) {
        return path === item.url || path.startsWith(item.url + "/");
      }

      if (item.children?.length) {
        const nested = flattenMenuItems([item]);
        return nested.some(
          (n) => path === n.url || path.startsWith(n.url + "/")
        );
      }

      return false;
    });

    return idx >= 0 ? idx : 0;
  }, [path, topItems]);

  const [tabIndex, setTabIndex] = useState(computedIndex);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  useEffect(() => {
    setTabIndex(computedIndex);
  }, [computedIndex]);

  useEffect(() => {
    const flat = flattenMenuItems(topItems);
    const firstUrl = flat[0]?.url;
    if (!firstUrl) return;

    const pathMatches = flat.some(
      (item) =>
        path === item.url || (item.url && path.startsWith(item.url + "/"))
    );

    if (!pathMatches) {
      navigate(firstUrl);
    }
  }, [path, topItems, navigate]);

  const handleTabClick = (event, index) => {
    const item = displayItems[index];
    setTabIndex(index);

    if (item.children?.length) {
      setAnchorEl(event.currentTarget);
      setActiveMenu(item);
    } else if (item.url) {
      navigate(item.url);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const handleNavigate = (url) => {
    navigate(url);
    handleCloseMenu();
  };

  return (
    <>
      {/*
       * The same header the section pages get, from the same module — card,
       * ornament, badge, heading and pills all come from there, so this strip
       * cannot drift into a design of its own. Only its contents are ours.
       *
       * The badge carries the same icon the sidebar's Admin entry uses, so the
       * two surfaces agree about what admin looks like.
       */}
      <PageHeader icon={IconShieldLock} title="Admin" sub="Everything you manage">
        {/*
         * The same pill rail the section pages use. It replaces MUI's <Tabs>,
         * which brought its own indicator, its own small scroll chevrons and a
         * two-line icon-over-label layout that no other strip in the app has.
         * Entries with children still open the dropdown below; the chevron beside
         * the label is what says so.
         */}
        <ScrollRail label="Admin sections" activeKey={tabIndex}>
          {displayItems.map((item, idx) => {
            const Icon = item.icon;
            const isActive = idx === tabIndex;
            const isOpen = activeMenu?.id === item.id;
            // Skipped for the first pill and for the one right after the active
            // pill, so the rule never sits beside a filled pill. Computed here
            // because it depends on the row, not on the pill.
            const divider = idx > 0 && !isActive && idx - 1 !== tabIndex;

            return (
              <Box
                key={item.id}
                role="tab"
                aria-selected={isActive}
                // Tells the rail which pill to bring into view.
                data-rail-active={isActive ? "true" : undefined}
                onClick={(e) => handleTabClick(e, idx)}
                sx={{ ...pillSx(theme, isActive, divider), cursor: "pointer" }}
              >
                {Icon && <Icon size={17} stroke={2} aria-hidden="true" />}
                {item.title}

                {item.children?.length ? (
                  <IconChevronDown
                    size={16}
                    stroke={2}
                    aria-hidden="true"
                    style={{
                      marginLeft: 2,
                      transition: "transform .25s ease",
                      transform: isOpen ? "rotate(180deg)" : "none",
                    }}
                  />
                ) : null}
              </Box>
            );
          })}
        </ScrollRail>
      </PageHeader>

      {/* ---------- SUBMENU ---------- */}
      {/*
       * Outside the header rather than inside it: this is a dropdown, not part of
       * the card. It would render correctly either way — MUI portals it out of the
       * DOM, so the card's `overflow: hidden` could not clip it — but keeping it
       * outside says what it is.
       */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            mt: 1.5,
            borderRadius: 4,
            minWidth: 300,
            boxShadow: theme.shadows[8],
            p: 2,
            background: 'background.paper',
            // Not optional beside `background`. MUI paints its elevation overlay
            // on Paper through `backgroundImage`, which `background` does not
            // clear, so in dark mode this menu would come out tinted rather than
            // the paper colour. Every other popper in the app sets it too.
            backgroundImage: 'none',
          },
        }}
      >
        {activeMenu?.children?.map((group, groupIndex) => (
          <Box key={group.id} sx={{ mb: 2 }}>
            {group.type === "collapse" && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.2,
                    px: 1,
                    color: 'secondary.main',
                  }}
                >
                  {group.icon && <group.icon size={18} stroke={1.5} />}

                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: 'secondary.main',
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {group.title}
                  </Typography>
                </Box>

                {/* Rows, not a second copy of ItemRow. The block that used to be
                    here was a near-verbatim duplicate of it, which is how the
                    two drifted into testing the path slightly differently. */}
                {group.children?.map((child) => (
                  <ItemRow
                    key={child.id}
                    item={child}
                    onNavigate={handleNavigate}
                    selectedUrl={selectedUrl}
                  />
                ))}
              </>
            )}

            {group.type === "item" && group.url && (
              <ItemRow
                item={group}
                onNavigate={handleNavigate}
                selectedUrl={selectedUrl}
              />
            )}

            {groupIndex !== activeMenu.children.length - 1 && (
              <Box
                sx={{
                  mt: 2,
                  height: 1,
                  bgcolor: 'divider',
                }}
              />
            )}
          </Box>
        ))}
      </Menu>
    </>
  );
}

export default AdminMenu;
